import * as React from 'react';
import Helmet from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as routes from '@configs/routes.json';

import { AuthenticationStatus, CredentialsProps } from '@store/types';
import './Login.scss';

import Welcome, { IWelcomeStateProps } from './Welcome';
import Announcement, { AnnouncementStateProps } from './Announcement';
import { useTheme } from '@hooks';
export interface LoginStateProps
  extends AnnouncementStateProps,
    IWelcomeStateProps {
  status: AuthenticationStatus;
  error: string | undefined;
}

interface LoginDispatchProps {
  logIn: (content: CredentialsProps) => void;
}

const Login: React.FC<LoginStateProps & LoginDispatchProps> = ({
  logIn,
  status,
  error: requestError,
  announcement,
  message: marketingMessage,
}) => {
  // When session times out, we might have a current theme set to one of the
  // brands. We clear the current-theme and set the theme in the next 2 lines.
  // TODO: Create a procedure to be called from `logout` action and when the
  // session times out, to set the application state to a reasonable initial
  // state.
  localStorage.removeItem('current-theme');
  useTheme();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [validationError, setValidationError] = React.useState('');
  const { t } = useTranslation();

  const onLogIn = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setValidationError(t('login_error_missingRequired'));
      return;
    }
    setValidationError('');
    logIn({ username: email, password });
  };

  let error = undefined;
  if (validationError) {
    error = validationError;
  } else if (requestError) {
    error = t(requestError);
  }

  return (
    <div className='login'>
      <Helmet>
        <title>{t('login_title')}</title>
      </Helmet>
      <h1>{t('title')}</h1>
      <Announcement announcement={announcement} />
      <div className='row flexible-height-row'>
        <div className='col-6 p-0'>
          <Welcome message={marketingMessage} />
        </div>

        <div className='col-6 pr-0'>
          <div className='login-forms'>
            <div className='credentials-form p-3'>
              <h2>{t('login_signIn')}</h2>
              {error ? <div className='error mb-2'>{error}</div> : null}
              <form>
                <div className='form-group'>
                  <label htmlFor='emailInput'>{t('login_email')}</label>
                  <input
                    type='email'
                    value={email}
                    autoComplete='username'
                    onChange={(e) => setEmail(e.target.value)}
                    className='form-control'
                    id='emailInput'
                  />
                </div>
                <div className='form-group'>
                  <label htmlFor='passwordInput'>{t('login_password')}</label>
                  <input
                    type='password'
                    value={password}
                    autoComplete='current-password'
                    onChange={(e) => setPassword(e.target.value)}
                    className='form-control'
                    id='passwordInput'
                  />
                </div>
                <div className='actions clearfix'>
                  <div className='float-right mb-2'>
                    {status === AuthenticationStatus.authenticating ? (
                      <button className='btn btn-primary btn-sm' disabled>
                        <span
                          className='spinner-border spinner-border-sm mr-2'
                          role='status'
                          aria-hidden='true'
                        ></span>
                        {t('login_signIn', { uppercase: true })}
                      </button>
                    ) : (
                      <button
                        className='btn btn-primary btn-sm'
                        onClick={onLogIn}
                      >
                        {t('login_signIn', { uppercase: true })}
                      </button>
                    )}
                  </div>
                  <div className='float-left'>
                    <div>{t('login_forgotPassword')}</div>
                    <NavLink
                      to={routes.app.resetPassword}
                      className='font-weight-normal fcolor--secondary-btn-hover'
                    >
                      {t('login_resetPassword')}
                    </NavLink>
                  </div>
                </div>
              </form>
            </div>
            <div className='sub-panel mt-2 p-3'>
              <div>
                <div className='heading font-family-heading'>
                  {t('login_dontHaveAccount')}
                </div>
                <NavLink
                  to={routes.app.createAccount}
                  className='fcolor--data-values font-weight-normal'
                >
                  {t('createUser_createAccount')}{' '}
                  <i className='fas fa-chevron-right'></i>
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
